<template>
  <b-modal id="createUserModal" ref="createUserModal" @close="onClose">
    <template slot="modal-title">{{ $t('createUser') }}</template>
    <b-form>
      <fieldset :disabled="createLoading">
        <b-row>
          <b-col cols="4">{{ $t('userType') }}</b-col>
          <b-col>
            <b-form-select v-model="userType" id="userType" :options="getUserTypes" class="mb-4" @change="onUserTypeChange" autofocus/>
          </b-col>
        </b-row>

        <div v-if="userType === 'shared'">
          <b-row class="mb-4">
            <b-col cols="4">{{ $t('username') }}</b-col>
            <b-col>
              <b-form-input type="text" :placeholder="$t('noEmailAddress')" v-model="newUser.username"/>
            </b-col>
          </b-row>

          <b-row class="mb-4">
            <b-col cols="4">{{ $t('school') }}</b-col>
            <b-col>
              <b-input-group v-if="newUser.schoolOrganizations && newUser.schoolOrganizations[0]">
                <b-form-input type="text" v-model="newUser.schoolOrganizations[0].name" readonly/>
                <b-input-group-prepend>
                  <organization-search ref="searchSchool"/>
                  <span class="input-group-text"><b-icon-search @click="$refs['searchSchool'].showModal(populateOrganization)"/></span>
                </b-input-group-prepend>
              </b-input-group>

              <b-input-group v-else>
                <b-form-input type="text" :placeholder="$t('clickOnSchoolSearch')" readonly/>
                <b-input-group-prepend>
                  <organization-search ref="searchSchool"/>
                  <span class="input-group-text"><b-icon-search @click="$refs['searchSchool'].showModal(populateOrganization)"/></span>
                </b-input-group-prepend>
              </b-input-group>
            </b-col>
          </b-row>

          <b-row class="mb-4" v-if="newUser.schoolOrganizations">
            <b-col cols="4">{{ $t('organizationNumber') }}</b-col>
            <b-col>
              <b-form-input
                v-if="newUser.schoolOrganizations && newUser.schoolOrganizations[0]"
                type="text" v-model="newUser.schoolOrganizations[0].number" readonly/>
              <b-form-input v-else type="text" readonly/>
            </b-col>
          </b-row>
        </div>

        <div v-if="userType === 'personal'">
          <b-row class="mb-4">
            <b-col cols="4">{{ $t('username') }} ({{ $t('email').toLowerCase() }})</b-col>
            <b-col>
              <b-form-input type="email" :placeholder="$t('enterEmailAddress')" v-model="newUser.username"/>
            </b-col>
          </b-row>

          <b-row class="mb-4">
            <b-col cols="4">{{ $t('firstName') }}</b-col>
            <b-col>
              <b-form-input type="text" v-model="newUser.firstName"/>
            </b-col>
          </b-row>

          <b-row class="mb-4">
            <b-col cols="4">{{ $t('lastName') }}</b-col>
            <b-col>
              <b-form-input type="text" v-model="newUser.lastName"/>
            </b-col>
          </b-row>

        </div>
        <Password id="password" :label="$t('password')" :value="password" v-model="password"/>
      </fieldset>
    </b-form>

    <div slot="modal-footer" class="col-md-12">
      <b-form>
        <fieldset :disabled="createLoading">
          <b-button variant="primary" class="float-lg-right mr-1" @click="createUser(true)" :disabled="disableUserCreateButton">{{ $t('create') }}</b-button>
          <b-button variant="secondary" class="float-lg-left" v-on:click="onClose">{{ $t('cancel') }}</b-button>
        </fieldset>
      </b-form>
    </div>

  </b-modal>
</template>
<script>
  import OrganizationSearch from '../../components/OrganizationSearch'
  import { mapGetters, mapState } from 'vuex'
  import UserService from '../../services/UserService'
  import Password from '../../components/Password'

  export default {
    name: 'CreateUserModal',
    components: {
      OrganizationSearch,
      Password
    },
    data () {
      return {
        userType: 'shared',
        newUser: {
          source: 'LOCAL',
          primaryRole: 'teacher', // Set to 'teacher' as default userType is shared
          username: null,
          firstName: null,
          lastName: null,
          additionalRoles: [ 'shared_user '], // Added since default userType is shared
          schoolOrganizations: null
        },
        password: null
      }
    },
    computed: {
      ...mapGetters('users', ['user', 'createLoading']),
      ...mapState('users', { user: state => state.user }),
      getUserTypes () {
        return [
          { value: 'shared', text: this.$t('userTypes.shared') },
          { value: 'personal', text: this.$t('userTypes.personal') }
        ]
      },
      disableUserCreateButton () {
        // Regex is borrowed from https://owasp.org/www-community/OWASP_Validation_Regex_Repository
        let emailRegEx = /^[a-zA-Z0-9_+&*-]+(?:\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,7}$/

        if (this.userType === 'personal') {
          if (
            this.newUser.username && emailRegEx.test(this.newUser.username) &&
            this.password && this.password.length >= 8 &&
            this.newUser.firstName && this.newUser.lastName
          ) {
            return false
          }
        } else if (this.userType === 'shared') {
          if (
            this.newUser.username && !emailRegEx.test(this.newUser.username) &&
            this.password && this.password.length >= 8 &&
            this.newUser.schoolOrganizations && this.newUser.schoolOrganizations.length > 0 && this.newUser.schoolOrganizations[0].number
          ) {
            return false
          }
        }
        return true
      }
    },
    methods: {
      onClose () {
        this.onUserTypeChange()
        this.$refs['createUserModal'].hide()
      },
      onUserTypeChange () {
        if (this.userType === 'shared') {
          this.newUser.primaryRole = 'teacher'
          this.newUser.additionalRoles = [ 'shared_user' ]
          this.newUser.firstName = null
          this.newUser.lastName = null
          this.newUser.schoolOrganizations = [
            {
              name: null,
              number: null
            }
          ]
        } else {
          this.newUser.primaryRole = 'student'
          this.newUser.additionalRoles = []
          this.newUser.schoolOrganizations = []
        }
        this.password = null
      },
      createUser (close) {
        if (this.userType === "shared") {
          this.newUser.firstName = this.newUser.username
          this.newUser.lastName = "Fellesbruker"
          this.newUser.fullName = this.newUser.firstName + " " + this.newUser.lastName
        } else {
          this.newUser.fullName = this.newUser.firstName + " " + this.newUser.lastName
        }

        let that = this
        this.$store.dispatch('users/create', this.newUser)
          .then(function () {
            UserService.changeUserPassword({
              userId: that.$store.state.users.user.userId,
              password: that.password
            })
          })
          .then(() => {
            if (close) {
              that.onClose()
              this.$router.push('/users/' + that.$store.state.users.user.userId)
            }
          })
          .catch(error => {
            console.log('error ' + error)
            if (error.response && error.response.status === 409) {
              this.showToastError(this.$t('userAlreadyExists'))
            } else {
              throw error
            }
          })
      },
      populateOrganization (organization) {
        this.newUser.schoolOrganizations = []
        this.newUser.schoolOrganizations[0] = {
          name: organization.name,
          number: organization.organizationNumber
        }
      },
      showToastError (error) {
        this.$bvToast.toast(error, {
          title: this.$t('errorOccurred'),
          toaster: 'b-toaster-top-center',
          variant: 'danger',
          autoHideDelay: 10000
        })
      },
      showModal () {
        this.$bvModal.show('createUserModal')
      }
    },
    i18n: {
      messages: {
        en: {
          'createAndClose': 'Create and close',
          'userAlreadyExists': 'User already exists',
          'enterEmailAddress': 'Enter a valid email address',
          'noEmailAddress': 'Valid user name, not email address'
        },
        nb: {
          'createAndClose': 'Opprett og lukk',
          'userAlreadyExists': 'Brukernavn er allerede i bruk',
          'enterEmailAddress': 'Må være gyldig e-postadresse',
          'noEmailAddress': 'Gyldig brukernavn, ikke e-postadresse'
        }
      }
    }
  }
</script>
